import QRCode from "qrcode";

/**
 * Generate QR code as data URL
 */
export async function generateQRCodeDataUrl(url: string): Promise<string> {
  return await QRCode.toDataURL(url, {
    width: 512,
    margin: 2,
    color: { dark: "#000000", light: "#ffffff" },
  });
}

/**
 * Pre-load a logo image for reuse across multiple labels
 */
export async function preloadLogoImage(logoUrl: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const logoImg = new Image();
    logoImg.crossOrigin = "anonymous";
    
    const timeoutId = setTimeout(() => {
      console.log("[Label Preload] Logo load timeout");
      resolve(null);
    }, 5000);

    logoImg.onload = () => {
      clearTimeout(timeoutId);
      console.log(`[Label Preload] Logo loaded: ${logoImg.naturalWidth}x${logoImg.naturalHeight}`);
      resolve(logoImg);
    };
    logoImg.onerror = () => {
      clearTimeout(timeoutId);
      console.log("[Label Preload] Logo load failed");
      resolve(null);
    };
    logoImg.src = logoUrl;
  });
}

/**
 * Draw full printable label to canvas (4"x2" at 300 DPI = 1200x600px)
 * Avery 5163 landscape format - Smart Box Sticker design
 * @param logoImg - Pre-loaded logo image (use preloadLogoImage) or null
 */
export async function drawLabelToCanvas(
  boxNumber: string,
  qrDataUrl: string,
  logoImg?: HTMLImageElement | null
): Promise<string> {
  const drawStart = performance.now();
  console.log("[Label Draw] Starting label creation for:", boxNumber);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  // 4" x 2" at 300 DPI (landscape orientation for Avery 5163)
  const WIDTH = 1200;
  const HEIGHT = 600;
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  console.log("[Label Draw] Canvas dimensions:", WIDTH, "x", HEIGHT);

  // Background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // Layout constants - matching PDF design exactly
  const BORDER_WIDTH = 3;
  const LEFT_COL_WIDTH = 300; // QR/Logo column
  const HEADER_HEIGHT = 130; // Header with title + 3 instructions (increased for spacing)
  const CONTENT_HEIGHT = HEIGHT - HEADER_HEIGHT; // 470px
  const NOTES_HEIGHT = 290; // Notes section
  const ROOM_HEIGHT = CONTENT_HEIGHT - NOTES_HEIGHT; // Room section (180px)

  // Border settings
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = BORDER_WIDTH;

  // === OUTER BORDER ===
  ctx.strokeRect(BORDER_WIDTH / 2, BORDER_WIDTH / 2, WIDTH - BORDER_WIDTH, HEIGHT - BORDER_WIDTH);

  // === HEADER SECTION (top area - spans full width) ===
  
  // "SMART BOX STICKER" title - large bold
  ctx.font = "bold 44px Inter, sans-serif";
  ctx.fillStyle = "#000000";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("SMART BOX STICKER", 24, 14);

  // Instructions stacked vertically below title (with proper spacing)
  ctx.font = "400 20px Inter, sans-serif";
  ctx.fillText("1. SCAN this code", 24, 58);
  ctx.fillText("2. SNAP a photo of what's inside", 24, 80);
  ctx.fillText("3. FIND it instantly months later", 24, 102);

  // "NEVER FORGET WHAT'S IN THIS BOX" tagline (right side)
  ctx.font = "bold 22px Inter, sans-serif";
  ctx.textAlign = "right";
  ctx.fillText("NEVER FORGET", WIDTH - 24, 30);
  ctx.fillText("WHAT'S IN THIS BOX", WIDTH - 24, 56);

  // Horizontal line below header
  ctx.beginPath();
  ctx.moveTo(0, HEADER_HEIGHT);
  ctx.lineTo(WIDTH, HEADER_HEIGHT);
  ctx.stroke();

  // === LEFT COLUMN - QR Code section (top part) ===
  
  // Vertical divider for left column - full height from header to bottom
  ctx.beginPath();
  ctx.moveTo(LEFT_COL_WIDTH, HEADER_HEIGHT);
  ctx.lineTo(LEFT_COL_WIDTH, HEIGHT);
  ctx.stroke();

  // "my.endless-storage.com" URL below header line - italic style
  ctx.font = "italic 14px Inter, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("my.endless-storage.com", LEFT_COL_WIDTH / 2, HEADER_HEIGHT + 14);

  // Load and draw QR code
  console.log("[Label Draw] Loading QR image...");
  const qrLoadStart = performance.now();
  const qrImg = new Image();
  await new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("QR image load timeout"));
    }, 5000);

    qrImg.onload = () => {
      clearTimeout(timeoutId);
      console.log(
        `[Label Draw] QR image loaded in ${(performance.now() - qrLoadStart).toFixed(2)}ms`
      );
      resolve();
    };
    qrImg.onerror = (err) => {
      clearTimeout(timeoutId);
      console.error("[Label Draw] QR image failed to load:", err);
      reject(err);
    };
    qrImg.src = qrDataUrl;
  });

  // Draw QR code - centered in left column
  const qrSize = 170;
  const qrX = (LEFT_COL_WIDTH - qrSize) / 2;
  const qrY = HEADER_HEIGHT + 34;
  ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
  console.log("[Label Draw] QR drawn at position:", qrX, qrY, "size:", qrSize);

  // Box number below QR code
  ctx.font = 'bold 20px "Roboto Mono", monospace';
  ctx.textAlign = "center";
  ctx.fillText(boxNumber, LEFT_COL_WIDTH / 2, qrY + qrSize + 16);

  // Horizontal divider between QR and Logo sections in left column
  const logoSectionY = HEADER_HEIGHT + NOTES_HEIGHT;
  ctx.beginPath();
  ctx.moveTo(0, logoSectionY);
  ctx.lineTo(LEFT_COL_WIDTH, logoSectionY);
  ctx.stroke();

  // === LEFT COLUMN - Logo section (bottom part) ===
  
  console.log("[Label Draw] Logo image:", logoImg ? "present" : "none");
  if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
    try {
      const maxLogoHeight = 100;
      const maxLogoWidth = 240;
      let logoHeight = logoImg.naturalHeight;
      let logoWidth = logoImg.naturalWidth;

      if (logoHeight > maxLogoHeight) {
        logoWidth = (logoWidth * maxLogoHeight) / logoHeight;
        logoHeight = maxLogoHeight;
      }
      if (logoWidth > maxLogoWidth) {
        logoHeight = (logoHeight * maxLogoWidth) / logoWidth;
        logoWidth = maxLogoWidth;
      }

      // Center logo in bottom-left cell
      const logoX = (LEFT_COL_WIDTH - logoWidth) / 2;
      const logoY = logoSectionY + (ROOM_HEIGHT - logoHeight) / 2;
      ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
      console.log("[Label Draw] Logo drawn at:", logoX, logoY, "scaled to:", logoWidth, "x", logoHeight);
    } catch (err) {
      console.error("[Label Draw] Logo error:", err);
    }
  }

  // === RIGHT COLUMN - NOTES SECTION ===
  
  const rightColStartX = LEFT_COL_WIDTH + 20;
  const notesStartY = HEADER_HEIGHT + 12;
  
  // "NOTES" header
  ctx.font = "bold 28px Inter, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("NOTES", rightColStartX, notesStartY);

  // 4 solid horizontal lines for notes
  ctx.lineWidth = 1.5;
  const noteLineStartY = notesStartY + 50;
  const noteLineSpacing = 58;
  const noteLineEndX = WIDTH - 20;
  
  for (let i = 0; i < 4; i++) {
    const y = noteLineStartY + (i * noteLineSpacing);
    ctx.beginPath();
    ctx.moveTo(rightColStartX, y);
    ctx.lineTo(noteLineEndX, y);
    ctx.stroke();
  }

  // Horizontal divider between Notes and Room sections
  ctx.lineWidth = BORDER_WIDTH;
  ctx.beginPath();
  ctx.moveTo(LEFT_COL_WIDTH, logoSectionY);
  ctx.lineTo(WIDTH, logoSectionY);
  ctx.stroke();

  // === RIGHT COLUMN - ROOM CHECKBOXES SECTION ===
  
  const roomStartY = logoSectionY + 16;
  
  // "ROOM" header - bold
  ctx.font = "bold 24px Inter, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("ROOM", rightColStartX, roomStartY + 6);

  // Checkbox drawing helper - proper alignment
  const drawCheckbox = (x: number, y: number, label: string, hasLine: boolean = false) => {
    const boxSize = 20;
    
    // Draw checkbox
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, boxSize, boxSize);
    
    // Draw label - vertically centered with checkbox
    if (label) {
      ctx.font = "400 18px Inter, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(label, x + boxSize + 10, y + boxSize / 2);
      ctx.textBaseline = "top";
    }
    
    if (hasLine) {
      // Draw write-in line
      const lineStartX = x + boxSize + 10;
      const lineEndX = noteLineEndX;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(lineStartX, y + boxSize);
      ctx.lineTo(lineEndX, y + boxSize);
      ctx.stroke();
    }
  };

  // Two columns: preset rooms | write-in fields
  const checkboxCol1X = rightColStartX + 90; // After "ROOM" label
  const checkboxCol2X = rightColStartX + 380; // Write-in column
  const checkboxRowHeight = 34;
  const checkboxStartY = roomStartY + 32;

  // Column 1: Preset rooms (4 rows)
  drawCheckbox(checkboxCol1X, checkboxStartY, "BEDROOM");
  drawCheckbox(checkboxCol1X, checkboxStartY + checkboxRowHeight, "KITCHEN");
  drawCheckbox(checkboxCol1X, checkboxStartY + checkboxRowHeight * 2, "BATHROOM");
  drawCheckbox(checkboxCol1X, checkboxStartY + checkboxRowHeight * 3, "LIVING ROOM");

  // Column 2: Write-in fields (4 rows)
  drawCheckbox(checkboxCol2X, checkboxStartY, "", true);
  drawCheckbox(checkboxCol2X, checkboxStartY + checkboxRowHeight, "", true);
  drawCheckbox(checkboxCol2X, checkboxStartY + checkboxRowHeight * 2, "", true);
  drawCheckbox(checkboxCol2X, checkboxStartY + checkboxRowHeight * 3, "", true);

  console.log("[Label Draw] Converting canvas to data URL...");
  const dataUrl = canvas.toDataURL("image/png");
  console.log(
    `[Label Draw] Label complete in ${(performance.now() - drawStart).toFixed(2)}ms, data URL length: ${dataUrl.length}`
  );

  return dataUrl;
}

/**
 * Generate labels and package into ZIP or PDF
 */
export async function generateLabelsPackage(
  labels: Array<{ code: string; labelDataUrl: string }>,
  exportFormat: "zip" | "pdf",
  filenamePrefix: string
): Promise<Blob> {
  if (exportFormat === "pdf") {
    // Dynamic import to avoid loading jspdf when not needed
    const { jsPDF } = await import("jspdf");

    // Avery 5163 layout (2 columns x 5 rows = 10 labels per sheet)
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "in",
      format: "letter",
    });

    const COLS = 2;
    const ROWS = 5;
    const LABELS_PER_PAGE = COLS * ROWS;
    const LABEL_WIDTH = 4;
    const LABEL_HEIGHT = 2;
    const MARGIN_LEFT = 0.15625;
    const MARGIN_TOP = 0.5;
    const GAP_X = 0.1875;

    for (let i = 0; i < labels.length; i++) {
      const positionOnPage = i % LABELS_PER_PAGE;
      const col = positionOnPage % COLS;
      const row = Math.floor(positionOnPage / COLS);

      if (i > 0 && positionOnPage === 0) {
        pdf.addPage();
      }

      const x = MARGIN_LEFT + col * (LABEL_WIDTH + GAP_X);
      const y = MARGIN_TOP + row * LABEL_HEIGHT;

      pdf.addImage(labels[i].labelDataUrl, "PNG", x, y, LABEL_WIDTH, LABEL_HEIGHT);
    }

    return pdf.output("blob");
  } else {
    // ZIP format
    const JSZip = (await import("jszip")).default;
    const zip = new JSZip();

    for (const { code, labelDataUrl } of labels) {
      const base64 = labelDataUrl.split(",")[1];
      zip.file(`${code}.png`, base64, { base64: true });
    }

    return await zip.generateAsync({ type: "blob" });
  }
}
